import { memo, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { useTranslation } from '@hooks';
import useMediaQuery from '@mui/material/useMediaQuery';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import { Typography } from '@components/Typography';
import Bar from '@components/Charts/Bar';
import { ConversationMap } from '@constants/conversations';
import { I18N_DATE_FORMAT, getChartDateFormat } from '@helpers/dates';
import { styled } from '@mui/system';

const PaperCss = styled(Paper)({
    width: '100%',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '8px',
    marginBottom: '16px',
    '@media print': {
        breakInside: 'avoid',
    },
});

const ChartWrapper = styled('div')({
    '@media print': {
        height: '280px !important',
    },
});

const chartItemOptions = {
    categoryPercentage: 1.0,
    barPercentage: 0.7,
    borderWidth: 0,
    borderRadius: 8,
};

const createChartData = (labels, data, t) => {
    return {
        labels,
        datasets: [
            {
                label: t('common.archived'),
                data: data.map(d => d[ConversationMap.Archived]),
                backgroundColor: '#D0D0D0',
                hoverBackgroundColor: '#D0D0D0',
                ...chartItemOptions,
            },
            {
                label: 'Instagram',
                data: data.map(d => d[ConversationMap.Instagram]),
                backgroundColor: '#F3F7D7',
                hoverBackgroundColor: '#F3F7D7',
                ...chartItemOptions,
            },
            {
                label: 'Messenger',
                data: data.map(d => d[ConversationMap.Messenger]),
                backgroundColor: '#E7EEB3',
                hoverBackgroundColor: '#E7EEB3',
                ...chartItemOptions,
            },
            {
                label: 'WhatsApp Business',
                data: data.map(d => d[ConversationMap.WhatsAppBusiness]),
                backgroundColor: '#62828D',
                hoverBackgroundColor: '#62828D',
                ...chartItemOptions,
            },
            {
                label: 'WhatsApp',
                data: data.map(d => d[ConversationMap.WhatsApp]),
                backgroundColor: '#BDE0CC',
                hoverBackgroundColor: '#BDE0CC',
                ...chartItemOptions,
            },
            {
                label: 'SMS',
                data: data.map(d => d[ConversationMap.SMS]),
                backgroundColor: '#98CBAD',
                hoverBackgroundColor: '#98CBAD',
                ...chartItemOptions,
            },
            {
                label: 'Live-chat',
                data: data.map(d => d[ConversationMap['Live-chat']]),
                backgroundColor: '#1F4C5C',
                hoverBackgroundColor: '#1F4C5C',
                ...chartItemOptions,
            },
        ],
    };
};

const ConversationsTrend = memo(({ data, language, grouping }) => {
    const { t } = useTranslation();
    const isMobile = useMediaQuery('@media (max-width: 1024px)');

    const formattedData = useMemo(() => {
        return data.map(item => {
            return item.breakdown.reduce((acc, curr) => {
                acc[curr.channelType === null ? ConversationMap.Archived : curr.channelType] = curr.count;
                return acc;
            }, {});
        });
    }, [data]);

    const labels = useMemo(() => {
        return data.map(d =>
            format(parseISO(d.date), getChartDateFormat(grouping), { locale: I18N_DATE_FORMAT[language] }),
        );
    }, [language, data, grouping]);

    return (
        <PaperCss elevation={1}>
            <Typography variant="title1" sx={{ marginBottom: '50px' }}>
                {t('AnalyticsConversations.conversationsTrend')}
            </Typography>
            <TableContainer>
                <ChartWrapper
                    id="conversations-chart-wrapper"
                    style={{
                        position: 'relative',
                        height: isMobile ? '400px' : '280px',
                        ...(isMobile && { width: '900px' }),
                    }}
                >
                    {labels.length ? (
                        <Bar
                            parentId="conversations-chart-wrapper"
                            data={createChartData(labels, formattedData, t)}
                            labels={labels}
                            legends={t('AnalyticsReviews.totalResponses')}
                        />
                    ) : null}
                </ChartWrapper>
            </TableContainer>
        </PaperCss>
    );
});

ConversationsTrend.displayName = 'SocialImpressionsTrend';

export default ConversationsTrend;
